import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ExamplePromptCard from './components/ExamplePromptCard';
import PromptQualityIndicator from './components/PromptQualityIndicator';
import GenerationSettingsPanel from './components/GenerationSettingsPanel';
import PromptTipsSidebar from './components/PromptTipsSidebar';
import RecentPromptsSection from './components/RecentPromptsSection';

const PromptInput = () => {
  const navigate = useNavigate();
  const [promptText, setPromptText] = useState('');
  const [qualityScore, setQualityScore] = useState(0);
  const [qualityFeedback, setQualityFeedback] = useState('');
  const [settings, setSettings] = useState({
    frontend: 'react',
    backend: 'nodejs',
    database: 'postgresql',
    auth: 'jwt'
  });

  const examplePrompts = [
    {
      title: 'E-commerce Platform',
      description: 'Full-featured online store with product management and checkout',
      prompt: 'Create an e-commerce platform with product catalog, shopping cart, user authentication, order management, payment integration, admin dashboard for inventory management, customer reviews and ratings, and email notifications for order updates.',
      icon: 'ShoppingCart',
      category: 'E-commerce'
    },
    {
      title: 'Social Media App',
      description: 'Connect users with posts, comments, and real-time messaging',
      prompt: 'Build a social media application where users can create profiles, post updates with images and videos, follow other users, like and comment on posts, send direct messages, receive real-time notifications, and have a personalized feed based on their connections.',
      icon: 'Users',
      category: 'Social'
    },
    {
      title: 'Project Management Tool',
      description: 'Organize tasks, teams, and workflows efficiently',
      prompt: 'Develop a project management tool with task creation and assignment, project boards with drag-and-drop functionality, team collaboration features, time tracking, file attachments, progress reports, calendar integration, and role-based access control for team members and managers.',
      icon: 'Briefcase',
      category: 'Business'
    },
    {
      title: 'Learning Management System',
      description: 'Educational platform for courses and student progress tracking',
      prompt: 'Create a learning management system where instructors can create courses with video lessons and quizzes, students can enroll and track their progress, discussion forums for each course, assignment submissions, grade tracking, certificates upon completion, and analytics dashboard for instructors.',
      icon: 'GraduationCap',
      category: 'Education'
    },
    {
      title: 'Healthcare Appointment System',
      description: 'Manage patient appointments and medical records',
      prompt: 'Build a healthcare appointment system with patient registration, doctor profiles and availability, appointment booking and rescheduling, medical records management, prescription tracking, appointment reminders via email and SMS, video consultation integration, and HIPAA-compliant data storage.',
      icon: 'Heart',
      category: 'Healthcare'
    },
    {
      title: 'Real Estate Listing Platform',
      description: 'Property listings with search and virtual tours',
      prompt: 'Develop a real estate platform where agents can list properties with photos and descriptions, users can search and filter properties by location, price, and features, save favorite listings, schedule property viewings, virtual tour integration, mortgage calculator, contact forms for inquiries, and map-based property search.',
      icon: 'Home',
      category: 'Real Estate'
    }
  ];

  const recentPrompts = [
    {
      text: 'Create a task management app with team collaboration features and real-time updates',
      timestamp: '2 hours ago',
      projectType: 'Business Tool'
    },
    {
      text: 'Build a recipe sharing platform where users can post recipes, rate dishes, and follow chefs',
      timestamp: 'Yesterday',
      projectType: 'Social Platform'
    },
    {
      text: 'Develop a fitness tracking app with workout plans, progress charts, and nutrition logging',
      timestamp: '3 days ago',
      projectType: 'Health & Fitness'
    }
  ];

  useEffect(() => {
    calculateQualityScore(promptText);
  }, [promptText]);

  const calculateQualityScore = (text) => {
    if (!text?.trim()) {
      setQualityScore(0);
      setQualityFeedback('Start typing to see quality assessment');
      return;
    }

    let score = 0;
    let feedback = [];

    if (text?.length > 50) score += 20;
    if (text?.length > 100) score += 20;
    if (text?.length > 200) score += 10;

    const hasFeatures = /feature|function|capability|ability|can|should|must/i?.test(text);
    if (hasFeatures) {
      score += 15;
    } else {
      feedback?.push('Mention specific features');
    }

    const hasUsers = /user|customer|admin|vendor|client|member/i?.test(text);
    if (hasUsers) {
      score += 15;
    } else {
      feedback?.push('Define user types');
    }

    const hasData = /data|database|store|save|record|information/i?.test(text);
    if (hasData) {
      score += 10;
    } else {
      feedback?.push('Describe data requirements');
    }

    const hasPurpose = /create|build|develop|make|design/i?.test(text);
    if (hasPurpose) score += 10;

    setQualityScore(Math.min(score, 100));
    setQualityFeedback(
      feedback?.length > 0
        ? `Consider adding: ${feedback?.join(', ')}`
        : 'Great prompt! Ready to generate your project.'
    );
  };

  const handleExampleClick = (prompt) => {
    setPromptText(prompt);
  };

  const handleGenerate = () => {
    if (promptText?.trim() && qualityScore >= 40) {
      navigate('/code-generation', {
        state: {
          prompt: promptText,
          settings: settings,
          timestamp: new Date()?.toISOString()
        }
      });
    }
  };

  const characterCount = promptText?.length;
  const maxCharacters = 2000;

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation />
      <div className="content-wrapper">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-10">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3">
              Describe Your Application
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
              Tell us what you want to build, and we'll generate a complete project structure with code
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-4 md:space-y-5 lg:space-y-6">
              <div className="bg-card border border-border rounded-lg p-4 md:p-5 lg:p-6">
                <label htmlFor="prompt-input" className="block text-sm md:text-base font-semibold mb-3 md:mb-4">
                  Project Description
                </label>
                <textarea
                  id="prompt-input"
                  value={promptText}
                  onChange={(e) => setPromptText(e?.target?.value)}
                  placeholder="Example: Create an e-commerce platform with user authentication, product catalog, shopping cart, payment integration, order management, and admin dashboard. Users should be able to browse products, add items to cart, checkout securely, and track their orders. Admins need inventory management, sales analytics, and customer management features."
                  className="w-full min-h-[200px] md:min-h-[250px] lg:min-h-[300px] p-4 bg-muted border border-border rounded-lg text-sm md:text-base resize-none focus:outline-none focus:ring-2 focus:ring-ring scrollbar-thin"
                  maxLength={maxCharacters}
                />
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs md:text-sm text-muted-foreground">
                    {characterCount} / {maxCharacters} characters
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => setPromptText('')}
                      disabled={!promptText}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>

              <PromptQualityIndicator score={qualityScore} feedback={qualityFeedback} />

              <RecentPromptsSection
                recentPrompts={recentPrompts}
                onSelectPrompt={handleExampleClick}
              />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base md:text-lg font-semibold">Example Prompts</h3>
                  <span className="text-xs md:text-sm text-muted-foreground">Click to use</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {examplePrompts?.map((example, index) => (
                    <ExamplePromptCard
                      key={index}
                      {...example}
                      onClick={handleExampleClick}
                    />
                  ))}
                </div>
              </div>

              <div className="sticky bottom-4 md:bottom-6 bg-card border border-border rounded-lg p-4 md:p-5 shadow-lg">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Sparkles"
                    iconPosition="left"
                    onClick={handleGenerate}
                    disabled={!promptText?.trim() || qualityScore < 40}
                    className="flex-1"
                  >
                    Generate Project
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Save"
                    iconPosition="left"
                    disabled={!promptText?.trim()}
                  >
                    Save Draft
                  </Button>
                </div>
                {qualityScore < 40 && promptText?.trim() && (
                  <div className="flex items-start gap-2 mt-3 text-warning">
                    <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                    <p className="text-xs md:text-sm">
                      Please provide more details to improve prompt quality (minimum 40% required)
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4 md:space-y-5 lg:space-y-6">
              <GenerationSettingsPanel
                settings={settings}
                onSettingsChange={setSettings}
              />
              <PromptTipsSidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptInput;